import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { MagnifyingGlassIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  ConfirmDialog,
  Field,
  FormActions,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Tab = 'oee' | 'downtime' | 'reasons';

interface Figures {
  scheduledMinutes: number;
  plannedStopMinutes: number;
  plannedProductionMinutes: number;
  unplannedStopMinutes: number;
  runMinutes: number;
  bookedMinutes: number;
  unaccountedMinutes: number;
  processedQuantity: number;
  goodQuantity: number;
  rejectedQuantity: number;
  reworkQuantity: number;
  availability: number | null;
  performance: number | null;
  quality: number | null;
  oee: number | null;
}
interface CenterRow extends Figures {
  workCenterId: string;
  code: string;
  name: string;
  capacity: number;
  hoursPerDay: number;
  scheduledDays: number;
  stops: number;
  warnings: string[];
}
interface OeeReport {
  dateFrom: string;
  dateTo: string;
  days: number;
  overall: Figures;
  workCenters: CenterRow[];
  losses: { reasonId: string; code: string; name: string; isPlanned: boolean; minutes: number; stops: number }[];
  trend: { date: string; oee: number | null; availability: number | null; performance: number | null; quality: number | null }[];
}
interface Stop {
  id: string;
  workCenterId: string;
  workCenterCode: string;
  workCenterName: string;
  reasonId: string;
  reasonName: string;
  isPlanned: boolean;
  startedAt: string;
  endedAt: string | null;
  open: boolean;
  minutes: number;
  workOrderNumber: string | null;
  notes: string | null;
  reportedBy: string | null;
}
interface StopPage {
  data: Stop[];
  total: number;
  totalPages: number;
}
interface Summary {
  downNow: number;
  unplannedMinutesToday: number;
  unplannedMinutesThisMonth: number;
  stopsThisMonth: number;
  topReasonThisMonth: { name: string; minutes: number } | null;
}
interface Reason {
  id: string;
  code: string;
  name: string;
  isPlanned: boolean;
  isActive: boolean;
  stops: number;
}
interface Center {
  id: string;
  code: string;
  name: string;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const pad = (value: number) => String(value).padStart(2, '0');
const dayOf = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const daysAgo = (count: number) => {
  const date = new Date();
  date.setDate(date.getDate() - count);
  return dayOf(date);
};
// The value a datetime-local input shows for a moment
const localInput = (value: Date | string) => {
  const date = new Date(value);
  return `${dayOf(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const formatMoment = (value?: string | null) =>
  value ? new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
const formatDay = (value: string) => new Date(`${value}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
const duration = (minutes: number) => {
  const whole = Math.round(minutes);
  if (whole < 60) return `${whole} min`;
  return `${formatNumber(Math.floor(whole / 60))} h${whole % 60 ? ` ${whole % 60} min` : ''}`;
};
const percent = (value: number | null) => (value === null ? '—' : `${(value * 100).toFixed(1)}%`);
const tone = (value: number | null) => (value === null ? 'text-gray-400' : value >= 0.85 ? 'text-green-700' : value >= 0.6 ? 'text-amber-700' : 'text-red-600');
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

const oeeApi = {
  report: async (params: object): Promise<OeeReport> => (await api.get('/manufacturing/oee', { params: clean(params) })).data,
  stops: async (params: object): Promise<StopPage> => (await api.get('/manufacturing/downtime', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/manufacturing/downtime/summary')).data,
  saveStop: async (id: string | null, data: object): Promise<Stop> => (id ? await api.put(`/manufacturing/downtime/${id}`, data) : await api.post('/manufacturing/downtime', data)).data,
  closeStop: async (id: string): Promise<Stop> => (await api.post(`/manufacturing/downtime/${id}/close`, {})).data,
  removeStop: async (id: string): Promise<void> => {
    await api.delete(`/manufacturing/downtime/${id}`);
  },
  reasons: async (): Promise<Reason[]> => (await api.get('/manufacturing/downtime-reasons')).data,
  createReason: async (data: object): Promise<Reason> => (await api.post('/manufacturing/downtime-reasons', data)).data,
  updateReason: async (id: string, data: object): Promise<Reason> => (await api.patch(`/manufacturing/downtime-reasons/${id}`, data)).data,
  removeReason: async (id: string): Promise<void> => {
    await api.delete(`/manufacturing/downtime-reasons/${id}`);
  },
  centers: async (): Promise<Center[]> => {
    const result = (await api.get('/master-data/work-centers', { params: { limit: 100 } })).data;
    return (Array.isArray(result) ? result : result?.data ?? []) as Center[];
  },
};

// ---------------------------------------------------------------- OEE over a period

const Gauge: React.FC<{ label: string; value: number | null; hint: string }> = ({ label, value, hint }) => (
  <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
    <p className={cn('mt-1 text-2xl font-semibold tabular-nums', tone(value))}>{percent(value)}</p>
    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
      <div className={cn('h-full rounded-full', value === null ? '' : value >= 0.85 ? 'bg-green-500' : value >= 0.6 ? 'bg-amber-500' : 'bg-red-500')} style={{ width: `${Math.min(100, Math.max(0, (value ?? 0) * 100))}%` }} />
    </div>
    <p className="mt-2 text-xs text-gray-500">{hint}</p>
  </div>
);

const OeePanel: React.FC<{ centers: Center[] }> = ({ centers }) => {
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(daysAgo(1));
  const [workCenterId, setWorkCenterId] = useState('');
  const valid = !!dateFrom && !!dateTo && dateFrom <= dateTo && dateTo <= dayOf(new Date());
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['oee', 'report', { dateFrom, dateTo, workCenterId }],
    queryFn: () => oeeApi.report({ dateFrom, dateTo, workCenterId }),
    enabled: valid,
    placeholderData: keepPreviousData,
  });
  const overall = data?.overall;
  const lostTotal = (data?.losses ?? []).reduce((sum, loss) => sum + loss.minutes, 0);
  const trend = (data?.trend ?? []).map((point) => ({ day: formatDay(point.date), OEE: point.oee === null ? null : Math.round(point.oee * 1000) / 10 }));
  const hasTrend = trend.some((point) => point.OEE !== null);

  return (
    <div className="space-y-5 p-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="From" htmlFor="oee-from">
          <input id="oee-from" type="date" max={dateTo || undefined} value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className={cn(inputClass, 'w-44')} />
        </Field>
        <Field label="To" htmlFor="oee-to">
          <input id="oee-to" type="date" min={dateFrom || undefined} max={dayOf(new Date())} value={dateTo} onChange={(event) => setDateTo(event.target.value)} className={cn(inputClass, 'w-44')} />
        </Field>
        <Field label="Work center" htmlFor="oee-center">
          <select id="oee-center" value={workCenterId} onChange={(event) => setWorkCenterId(event.target.value)} className={cn(inputClass, 'w-56')}>
            <option value="">All work centers</option>
            {centers.map((center) => (<option key={center.id} value={center.id}>{center.name}</option>))}
          </select>
        </Field>
        {!valid && <p className="pb-2 text-sm text-red-600">Choose a period that ends today or earlier.</p>}
      </div>

      {isError ? (
        <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'OEE could not be worked out.')}</p>
      ) : isLoading || !data || !overall ? (
        <p className="py-10 text-center text-sm text-gray-500">{valid ? 'Working it out…' : ''}</p>
      ) : (
        <div className={cn('space-y-5', isFetching && 'opacity-60')}>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Gauge label="OEE" value={overall.oee} hint="Availability × performance × quality" />
            <Gauge label="Availability" value={overall.availability} hint={`Ran ${duration(overall.runMinutes)} of ${duration(overall.plannedProductionMinutes)} planned`} />
            <Gauge label="Performance" value={overall.performance} hint="Pieces at standard time against the time it ran" />
            <Gauge label="Quality" value={overall.quality} hint={`${formatNumber(overall.goodQuantity)} good of ${formatNumber(overall.processedQuantity)} made`} />
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Work center</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Scheduled</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Planned stops</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Lost to stops</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Ran</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>On job cards</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Availability</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Performance</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Quality</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>OEE</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.workCenters.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-sm text-gray-500">No work centers yet. Add them under Master Data.</td>
                  </tr>
                ) : (
                  data.workCenters.map((row) => (
                    <tr key={row.workCenterId}>
                      <td className="px-4 py-2.5">
                        <p className="text-sm font-medium text-gray-900">{row.name}</p>
                        <p className="text-xs text-gray-500">
                          {row.code} · {formatNumber(row.hoursPerDay)} h a day{row.capacity > 1 ? ` × ${row.capacity} machines` : ''} · {row.scheduledDays} day(s)
                        </p>
                        {row.warnings.map((warning) => (<p key={warning} className="mt-0.5 text-xs text-amber-700">{warning}</p>))}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{duration(row.scheduledMinutes)}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{duration(row.plannedStopMinutes)}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{duration(row.unplannedStopMinutes)}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{duration(row.runMinutes)}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">
                        {duration(row.bookedMinutes)}
                        {row.unaccountedMinutes > 0 && <span className="block text-xs text-gray-500">{duration(row.unaccountedMinutes)} not accounted for</span>}
                      </td>
                      <td className={cn('whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums', tone(row.availability))}>{percent(row.availability)}</td>
                      <td className={cn('whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums', tone(row.performance))}>{percent(row.performance)}</td>
                      <td className={cn('whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums', tone(row.quality))}>{percent(row.quality)}</td>
                      <td className={cn('whitespace-nowrap px-4 py-2.5 text-right text-sm font-semibold tabular-nums', tone(row.oee))}>{percent(row.oee)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <section className="rounded-lg border border-gray-200 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Time stopped, by reason</h4>
              {data.losses.length === 0 ? (
                <p className="mt-3 text-sm text-gray-500">No stops were logged in this period.</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {data.losses.map((loss) => (
                    <li key={loss.reasonId}>
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="text-gray-900">
                          {loss.name} <span className={cn(badgeClass, 'ml-1', loss.isPlanned ? 'bg-gray-50 text-gray-600 ring-gray-500/20' : 'bg-red-50 text-red-700 ring-red-600/20')}>{loss.isPlanned ? 'Planned' : 'Unplanned'}</span>
                        </span>
                        <span className="whitespace-nowrap text-gray-700 tabular-nums">{duration(loss.minutes)} · {loss.stops} stop(s)</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
                        <div className={cn('h-full rounded-full', loss.isPlanned ? 'bg-gray-400' : 'bg-red-500')} style={{ width: `${lostTotal > 0 ? (loss.minutes / lostTotal) * 100 : 0}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="rounded-lg border border-gray-200 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">OEE by day</h4>
              {hasTrend ? (
                <div className="mt-3 h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trend} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} minTickGap={20} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                      <Tooltip formatter={(value) => [`${value}%`, 'OEE']} />
                      <Line type="monotone" dataKey="OEE" stroke="#4f46e5" strokeWidth={2} dot={{ r: 2 }} connectNulls={false} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="mt-3 text-sm text-gray-500">No day in this period has production booked on job cards, so there is nothing to plot.</p>
              )}
            </section>
          </div>

          <p className="text-xs text-gray-500">
            Scheduled time is the hours a day of the work center on its working days (Monday to Saturday when none are set), times its machines. Planned stops are taken out of it; unplanned stops are lost availability.
            Performance compares the pieces booked on job cards, at the run time of their operation, with the time the work center ran. Quality is the pieces good first time out of the pieces made.
          </p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- log or correct a stop

const StopForm: React.FC<{ stop: Stop | null; centers: Center[]; reasons: Reason[]; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ stop, centers, reasons, isPending, onCancel, onSubmit }) => {
  const [form, setForm] = useState({
    workCenterId: stop?.workCenterId ?? '',
    reasonId: stop?.reasonId ?? '',
    startedAt: localInput(stop?.startedAt ?? new Date()),
    endedAt: stop?.endedAt ? localInput(stop.endedAt) : '',
    notes: stop?.notes ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => {
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
  };
  // A reason that has been switched off stays on the stop it is on
  const offered = reasons.filter((reason) => reason.isActive || reason.id === stop?.reasonId);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const now = Date.now() + 60000;
    if (!form.workCenterId) next.workCenterId = 'Choose the work center';
    if (!form.reasonId) next.reasonId = 'Choose the reason';
    if (!form.startedAt) next.startedAt = 'Enter when it stopped';
    else if (new Date(form.startedAt).getTime() > now) next.startedAt = 'Not in the future';
    if (form.endedAt) {
      if (new Date(form.endedAt).getTime() > now) next.endedAt = 'Not in the future; leave it empty while it is still down';
      else if (form.startedAt && new Date(form.endedAt).getTime() <= new Date(form.startedAt).getTime()) next.endedAt = 'After it stopped';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      workCenterId: form.workCenterId,
      reasonId: form.reasonId,
      startedAt: new Date(form.startedAt).toISOString(),
      endedAt: form.endedAt ? new Date(form.endedAt).toISOString() : null,
      notes: form.notes.trim() || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Work center" htmlFor="dt-center" required error={errors.workCenterId}>
          <select id="dt-center" value={form.workCenterId} onChange={set('workCenterId')} className={cn(inputClass, errors.workCenterId && inputErrorClass)}>
            <option value="">Choose…</option>
            {centers.map((center) => (<option key={center.id} value={center.id}>{center.name}</option>))}
          </select>
        </Field>
        <Field label="Reason" htmlFor="dt-reason" required error={errors.reasonId} hint={offered.length === 0 ? 'Add reasons on the Reasons tab first' : undefined}>
          <select id="dt-reason" value={form.reasonId} onChange={set('reasonId')} className={cn(inputClass, errors.reasonId && inputErrorClass)}>
            <option value="">Choose…</option>
            {offered.map((reason) => (<option key={reason.id} value={reason.id}>{reason.name}{reason.isPlanned ? ' (planned)' : ''}</option>))}
          </select>
        </Field>
        <Field label="Stopped at" htmlFor="dt-start" required error={errors.startedAt}>
          <input id="dt-start" type="datetime-local" value={form.startedAt} onChange={set('startedAt')} className={cn(inputClass, errors.startedAt && inputErrorClass)} />
        </Field>
        <Field label="Ran again at" htmlFor="dt-end" error={errors.endedAt} hint="Leave empty while it is still down">
          <input id="dt-end" type="datetime-local" value={form.endedAt} onChange={set('endedAt')} className={cn(inputClass, errors.endedAt && inputErrorClass)} />
        </Field>
        <Field label="What happened" htmlFor="dt-notes" className="sm:col-span-2">
          <input id="dt-notes" type="text" maxLength={500} value={form.notes} onChange={set('notes')} className={inputClass} />
        </Field>
      </div>
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : stop ? 'Save Stop' : 'Log Stop'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- the reasons

const ReasonsPanel: React.FC<{ reasons: Reason[]; canCreate: boolean; canEdit: boolean; canDelete: boolean; onChanged: () => void }> = ({ reasons, canCreate, canEdit, canDelete, onChanged }) => {
  const [form, setForm] = useState({ code: '', name: '', isPlanned: false });
  const [error, setError] = useState('');
  const create = useMutation({
    mutationFn: () => oeeApi.createReason({ code: form.code.trim(), name: form.name.trim(), isPlanned: form.isPlanned }),
    onSuccess: (reason) => {
      toast.success(`${reason.name} added`);
      setForm({ code: '', name: '', isPlanned: false });
      onChanged();
    },
    onError: (createError) => toast.error(getApiErrorMessage(createError, 'The reason could not be added')),
  });
  const change = useMutation({
    mutationFn: ({ id, data }: { id: string; data: object }) => oeeApi.updateReason(id, data),
    onSuccess: onChanged,
    onError: (changeError) => toast.error(getApiErrorMessage(changeError, 'The reason could not be changed'), { duration: 7000 }),
  });
  const remove = useMutation({
    mutationFn: (reason: Reason) => oeeApi.removeReason(reason.id),
    onSuccess: (_result, reason) => {
      toast.success(`${reason.name} deleted`);
      onChanged();
    },
    onError: (removeError) => toast.error(getApiErrorMessage(removeError, 'The reason could not be deleted'), { duration: 7000 }),
  });
  const add = (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^[A-Za-z0-9][A-Za-z0-9-]{1,19}$/.test(form.code.trim())) return setError('The code is 2 to 20 letters, digits or hyphens');
    if (form.name.trim().length < 2) return setError('Name the reason');
    setError('');
    create.mutate();
  };

  return (
    <div className="space-y-4 p-4">
      {canCreate && (
        <form onSubmit={add} noValidate className="flex flex-wrap items-end gap-3">
          <Field label="Code" htmlFor="dr-code">
            <input id="dr-code" type="text" maxLength={20} value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })} placeholder="BRK" className={cn(inputClass, 'w-28')} />
          </Field>
          <Field label="Reason" htmlFor="dr-name" className="min-w-0 flex-1">
            <input id="dr-name" type="text" maxLength={100} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Machine breakdown" className={inputClass} />
          </Field>
          <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
            <input id="dr-planned" type="checkbox" checked={form.isPlanned} onChange={(event) => setForm({ ...form, isPlanned: event.target.checked })} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            Planned stop
          </label>
          <button type="submit" disabled={create.isPending} className={primaryButtonClass}>
            Add Reason
          </button>
          {error && <p className="w-full text-sm text-red-600">{error}</p>}
        </form>
      )}
      <p className="text-xs text-gray-500">A planned stop (no orders, preventive maintenance, a break) is taken out of the time the work center was meant to run. An unplanned stop (breakdown, no material, no power) counts against availability.</p>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Code</th>
              <th scope="col" className={thClass}>Reason</th>
              <th scope="col" className={thClass}>Kind</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Stops</th>
              <th scope="col" className={thClass}>In use</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {reasons.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">No downtime reasons yet. Add the ones your plant uses.</td>
              </tr>
            ) : (
              reasons.map((reason) => (
                <tr key={reason.id} className={cn(!reason.isActive && 'bg-gray-50/60')}>
                  <td className="whitespace-nowrap px-4 py-2.5 text-sm font-medium text-gray-900">{reason.code}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-900">{reason.name}</td>
                  <td className="whitespace-nowrap px-4 py-2.5"><span className={cn(badgeClass, reason.isPlanned ? 'bg-gray-50 text-gray-600 ring-gray-500/20' : 'bg-red-50 text-red-700 ring-red-600/20')}>{reason.isPlanned ? 'Planned' : 'Unplanned'}</span></td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatNumber(reason.stops)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <input type="checkbox" checked={reason.isActive} disabled={!canEdit || change.isPending} onChange={(event) => change.mutate({ id: reason.id, data: { isActive: event.target.checked } })} aria-label={`${reason.name} in use`} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right">
                    {canDelete && reason.stops === 0 && (
                      <button type="button" onClick={() => remove.mutate(reason)} disabled={remove.isPending} aria-label={`Delete ${reason.name}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- the page

const Oee: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('manufacturing.create');
  const canEdit = can('manufacturing.update');
  const canDelete = can('manufacturing.delete');
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<Tab>('oee');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [centerFilter, setCenterFilter] = useState('');
  const [stateFilter, setStateFilter] = useState<'open' | 'closed' | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Stop | null>(null);
  const [removing, setRemoving] = useState<Stop | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: centers = [] } = useQuery({ queryKey: ['oee', 'centers'], queryFn: oeeApi.centers });
  const { data: reasons = [] } = useQuery({ queryKey: ['oee', 'reasons'], queryFn: oeeApi.reasons });
  const { data: summary } = useQuery({ queryKey: ['oee', 'summary'], queryFn: oeeApi.summary });
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['oee', 'stops', { page, limit, search, workCenterId: centerFilter, state: stateFilter }],
    queryFn: () => oeeApi.stops({ page, limit, search, workCenterId: centerFilter, state: stateFilter }),
    placeholderData: keepPreviousData,
    enabled: tab === 'downtime',
  });

  const refresh = () => ['stops', 'summary', 'report', 'reasons'].forEach((key) => queryClient.invalidateQueries({ queryKey: ['oee', key] }));
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  const save = useMutation({
    mutationFn: (input: object) => oeeApi.saveStop(editing?.id ?? null, input),
    onSuccess: (stop) => {
      toast.success(editing ? 'Stop corrected' : stop.open ? `${stop.workCenterName} logged as down` : `${duration(stop.minutes)} of downtime logged on ${stop.workCenterName}`);
      refresh();
      closeForm();
      setTab('downtime');
    },
    onError: (saveError) => toast.error(getApiErrorMessage(saveError, 'The stop could not be saved'), { duration: 7000 }),
  });
  const runAgain = useMutation({
    mutationFn: (stop: Stop) => oeeApi.closeStop(stop.id),
    onSuccess: (stop) => {
      toast.success(`${stop.workCenterName} is running again after ${duration(stop.minutes)}`);
      refresh();
    },
    onError: (closeError) => toast.error(getApiErrorMessage(closeError, 'That could not be done')),
  });
  const remove = useMutation({
    mutationFn: (stop: Stop) => oeeApi.removeStop(stop.id),
    onSuccess: () => {
      toast.success('Stop removed');
      setRemoving(null);
      refresh();
    },
    onError: (removeError) => toast.error(getApiErrorMessage(removeError, 'The stop could not be removed')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || !!centerFilter || !!stateFilter;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const stats = [
    { label: 'Down now', value: formatNumber(summary?.downNow ?? 0), hint: 'Work centers with a stop still on' },
    { label: 'Lost today', value: duration(summary?.unplannedMinutesToday ?? 0), hint: 'Unplanned downtime' },
    { label: 'Lost this month', value: duration(summary?.unplannedMinutesThisMonth ?? 0), hint: `${formatNumber(summary?.stopsThisMonth ?? 0)} unplanned stop(s)` },
    { label: 'Biggest cause this month', value: summary?.topReasonThisMonth?.name ?? '—', hint: summary?.topReasonThisMonth ? duration(summary.topReasonThisMonth.minutes) : 'No unplanned stops' },
  ];
  const tabs: { key: Tab; label: string }[] = [
    { key: 'oee', label: 'OEE' },
    { key: 'downtime', label: 'Downtime log' },
    { key: 'reasons', label: 'Reasons' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">OEE &amp; Downtime</h1>
          <p className="mt-1 text-sm text-gray-500">When work centers stood still and why, and how effectively they ran: availability, performance and quality.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Log Downtime
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 truncate text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex gap-1 border-b border-gray-200 px-4 pt-3" role="tablist">
          {tabs.map((item) => (
            <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => setTab(item.key)} className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium', tab === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              {item.label}
            </button>
          ))}
        </div>

        {tab === 'oee' && <OeePanel centers={centers} />}
        {tab === 'reasons' && <ReasonsPanel reasons={reasons} canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} onChanged={refresh} />}
        {tab === 'downtime' && (
          <>
            <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by work center, reason, work order or notes" aria-label="Search downtime" className={cn(inputClass, 'pl-9')} />
              </div>
              <select value={centerFilter} onChange={(event) => { setCenterFilter(event.target.value); setPage(1); }} aria-label="Work center" className={cn(inputClass, 'lg:w-52')}>
                <option value="">All work centers</option>
                {centers.map((center) => (<option key={center.id} value={center.id}>{center.name}</option>))}
              </select>
              <select value={stateFilter} onChange={(event) => { setStateFilter(event.target.value as typeof stateFilter); setPage(1); }} aria-label="State" className={cn(inputClass, 'lg:w-40')}>
                <option value="">All stops</option>
                <option value="open">Still down</option>
                <option value="closed">Ended</option>
              </select>
            </div>
            <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className={thClass}>Work center</th>
                    <th scope="col" className={thClass}>Reason</th>
                    <th scope="col" className={thClass}>Stopped</th>
                    <th scope="col" className={thClass}>Ran again</th>
                    <th scope="col" className={cn(thClass, 'text-right')}>Length</th>
                    <th scope="col" className={thClass}>Notes</th>
                    <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {isError ? (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The downtime log could not be loaded.')}</td></tr>
                  ) : isLoading ? (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center">
                        <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No stop matches' : 'No downtime logged yet'}</p>
                        <p className="mt-1 text-sm text-gray-500">Log a stop when a work center stands still, and end it when it runs again.</p>
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className={cn('hover:bg-gray-50', row.open && 'bg-red-50/40')}>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">{row.workCenterName}</p>
                          <p className="text-xs text-gray-500">{row.workCenterCode}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {row.reasonName}
                          <span className={cn(badgeClass, 'ml-2', row.isPlanned ? 'bg-gray-50 text-gray-600 ring-gray-500/20' : 'bg-red-50 text-red-700 ring-red-600/20')}>{row.isPlanned ? 'Planned' : 'Unplanned'}</span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{formatMoment(row.startedAt)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.open ? <span className="font-medium text-red-600">Still down</span> : formatMoment(row.endedAt)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{duration(row.minutes)}</td>
                        <td className="max-w-xs truncate px-4 py-3 text-sm text-gray-700">{row.notes || '—'}{row.reportedBy && <span className="block text-xs text-gray-500">by {row.reportedBy}</span>}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          {row.open && canEdit && (
                            <button type="button" onClick={() => runAgain.mutate(row)} disabled={runAgain.isPending} className="mr-1 rounded-md px-2.5 py-1 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-600/30 hover:bg-green-50" aria-label={`${row.workCenterName} is running again`}>
                              Running Again
                            </button>
                          )}
                          {canEdit && (
                            <>
                              <button type="button" onClick={() => { setEditing(row); setFormOpen(true); }} title="Correct" aria-label={`Correct the stop of ${row.workCenterName}`} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={() => setRemoving(row)} title="Remove" aria-label={`Remove the stop of ${row.workCenterName}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
                <label className="flex items-center gap-2">
                  Rows per page
                  <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                    {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
                  </select>
                </label>
              </div>
              {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
            </div>
          </>
        )}
      </div>

      <Modal isOpen={formOpen} onClose={closeForm} title={editing ? 'Correct Stop' : 'Log Downtime'} size="lg">
        {formOpen && <StopForm key={editing?.id ?? 'new'} stop={editing} centers={centers} reasons={reasons} isPending={save.isPending} onCancel={closeForm} onSubmit={(input) => save.mutate(input)} />}
      </Modal>

      <ConfirmDialog
        isOpen={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing)}
        title="Remove this stop?"
        message={removing ? `The ${duration(removing.minutes)} stop of ${removing.workCenterName} (${removing.reasonName}) is taken out of the log and no longer counts in OEE.` : ''}
        confirmLabel="Remove Stop"
        isPending={remove.isPending}
      />
    </div>
  );
};

export default Oee;
